import { State } from 'xstate'
import { ERDecisionState, ERPlayState } from './GamePlay/types'
import {
  ERpState,
  RussianPokerMachineContext,
  RussianPokerMachineEvent,
} from './types'

type RpState = State<RussianPokerMachineContext, RussianPokerMachineEvent>

export const rpSelectors = {
  isBettingPhase: (state: RpState) =>
    state.matches({
      [ERpState.Joined]: { [ERPlayState.Playing]: ERPlayState.Betting },
    }),

  isSettlementPhase: (state: RpState) =>
    state.matches({
      [ERpState.Joined]: { [ERPlayState.Playing]: ERPlayState.Settlement },
    }),

  isFinishedPhase: (state: RpState) =>
    state.matches({
      [ERpState.Joined]: { [ERPlayState.Playing]: ERPlayState.Finished },
    }),

  boxOnTurn: (state: RpState) => state.context.boxOnTurn,

  isBoxOnTurn: (seatNumber: number) => (state: RpState) =>
    state.context.boxOnTurn === seatNumber,

  // True when decisions.activeBoxReady OR activeBoxRevealed — box has real cards, detail view is active.
  // Use this to gate the card flip animation start.
  isDecisionBoxReady: (state: RpState) => {
    const inReady = state.matches({
      [ERpState.Joined]: {
        [ERPlayState.Playing]: {
          [ERPlayState.Decisions]: ERDecisionState.ActiveBoxReady,
        },
      },
    })
    const inRevealed = state.matches({
      [ERpState.Joined]: {
        [ERPlayState.Playing]: {
          [ERPlayState.Decisions]: ERDecisionState.ActiveBoxRevealed,
        },
      },
    })
    return inReady || inRevealed
  },

  // True only when decisions.activeBoxRevealed — animation is done, interactive controls are allowed.
  isDecisionBoxRevealed: (state: RpState) =>
    state.matches({
      [ERpState.Joined]: {
        [ERPlayState.Playing]: {
          [ERPlayState.Decisions]: ERDecisionState.ActiveBoxRevealed,
        },
      },
    }),

  // True when the given seat is the active box with real cards ready (detail view can start).
  isDetailViewForBox: (seatNumber: number) => (state: RpState) =>
    rpSelectors.isDecisionBoxReady(state) &&
    state.context.boxOnTurn === seatNumber,

  // True when the given seat is the active box AND the card-flip animation is done.
  // Gate interactive controls (decision buttons, insurance) behind this selector.
  isInteractiveForBox: (seatNumber: number) => (state: RpState) =>
    rpSelectors.isDecisionBoxRevealed(state) &&
    state.context.boxOnTurn === seatNumber,
}
